import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'

import * as state from '../../../../../../../state'
import type { Component as WidgetComponent } from '../component'

import { FieldCollection } from './field-collection'

/** TODO:  */

export interface WidgetState<
  TType extends ents.core.mosaic.Widget.Type = ents.core.mosaic.Widget.Type,
> {
  /**
   * Widget {@link ents.core.mosaic.Widget.Rendering rendering} data that will
   * be shown in the {@link WidgetComponent}
   */
  rendering?: ents.core.mosaic.Widget.Rendering<TType>
  /**
   * A unique identifier for the widget within the page layout
   *
   * @see {@link state.mosaic.models.widget.Ref.Id}
   */
  refId: state.mosaic.models.widget.Ref.Id
  /**
   * Whether the overflow actions overlay is currently open
   */
  presentOverflowActions: boolean
  /**
   * Whether the {@link ents.core.mosaic.Widget.Rendering} is currently loading
   */
  isLoading?: boolean
  /**
   * Message shown in the widget when loading the
   * {@link ents.core.mosaic.Widget.Rendering rendering} fails
   */
  error?: string | Error
}

/** TODO:  */
@ngCore.Injectable({ providedIn: 'root' })
export class Widget<
  TType extends ents.core.mosaic.Widget.Type = ents.core.mosaic.Widget.Type,
> extends ComponentStore<WidgetState<TType>> {
  /** @see {@link FieldCollection} */
  public readonly fieldCollection
  /**  @see {@link ents.core.mosaic.Widget.Rendering} */
  public readonly renderingSg = this.selectSignal(_state => _state.rendering)
  /** @see {@link WidgetState.refId} */
  public readonly refIdSg = this.selectSignal(_state => _state.refId)
  /** @see {@link ents.core.mosaic.Widget.Rendering.overflowActions} */
  public readonly overflowActionsSg = ngCore.computed(
    (rendering = this.renderingSg()) => rendering?.overflowActions ?? [],
  )
  /**
   * Whether there are any overflow actions
   *
   * @see {@link ents.core.mosaic.Widget.Rendering.overflowActions}
   */
  public readonly hasOverflowActionsSg = ngCore.computed(
    (overflowActions = this.overflowActionsSg()) => overflowActions.length > 0,
  )
  /** @see {@link ents.core.mosaic.Widget.Rendering.type} */
  public readonly typeSg = ngCore.computed(
    (rendering = this.renderingSg()) => rendering?.type,
  )
  /** @see {@link ents.core.mosaic.Widget.Rendering.content} */
  public readonly contentSg = ngCore.computed(
    (rendering = this.renderingSg()) =>
      // NOTE: Typescript doesn't understand that if rendering if of type
      // Rendering<TType> then rendering.content is of type Content<TType>, so
      // we cast to help typescript understand
      rendering?.content as ents.core.mosaic.Widget.Content<TType> | undefined,
  )
  /** @see {@link ents.core.mosaic.Widget.Rendering.id} */
  public readonly renderingIdSg = ngCore.computed(
    (rendering = this.renderingSg()) => rendering?.id,
  )
  /** @see {@link ents.core.mosaic.Widget.Rendering.name} */
  public readonly nameSg = ngCore.computed(
    (rendering = this.renderingSg()) => rendering?.name,
  )
  /** @see {@link WidgetState.presentOverflowActions} */
  public readonly presentOverflowActionsSg = this.selectSignal(
    _state => _state.presentOverflowActions,
  )
  /** @see {@link WidgetState.isLoading} */
  public readonly isLoadingSg = this.selectSignal(
    _state => _state.isLoading ?? false,
  )
  /** @see {@link WidgetState.error} */
  public readonly errorSg = this.selectSignal(_state =>
    // TODO We should probably check here if it is instance of Error, not if it
    // is an object, but currently it is not saved as Error instance to state,
    // se we receive plain object here
    TypeChecker.isObject(_state.error) ? _state.error.message : _state.error,
  )

  constructor() {
    super({
      presentOverflowActions: false,
      // TODO: Not sure what this should be, but since this is required on
      // component input, I would assume it is define here
      refId: state.mosaic.models.widget.Ref.Id.FOR_PREVIEW,
    })
    this.fieldCollection = new FieldCollection({ widgetStore: this })
  }
}
